import UpdateButton from "../../../shared/UpdateButton/UpdateButton";
import {Input, InputGroup, toaster, Toggle, Whisper} from "rsuite";
import {useRef, useState, useEffect, useCallback} from "react";
import {BksApi} from "../../../../services/Bks.api";
import NotificationFactory from "../../../../shared/Notifications";
import moment, {Moment} from "moment";
import {PrimaryClockApi} from "../../../../services/PrimaryClock.api";
import {getTooltip} from "../../../../shared/Tooltip";
import {GearSettingsIcon} from "../../../../models/Icons.model";

interface BksTimeResponse {
  dawnTime?: string;
  duskTime?: string;
  time?: string;
}

interface SyncParamsResponse {
  ntpSyncEnabled: boolean;
}

interface BksTimeSectionProps {
  deviceId: string;
  onTabChange: any;
}

const BksTimeSection = ({deviceId, onTabChange}: BksTimeSectionProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [dawnTime, setDawnTime] = useState<string | null>(null);
  const [duskTime, setDuskTime] = useState<string | null>(null);
  const [deviceTime, setDeviceTime] = useState<Moment | null>(null);
  const [currentTime, setCurrentTime] = useState<string>("");
  const [userTimezone, setUserTimezone] = useState<string | null>(null);
  const [ntpSyncEnabled, setNtpSyncEnabled] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);

  const timeMomentRef = useRef<Moment | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const formatTimeHHmm = useCallback((timeString: string | undefined): string | null => {
    return timeString ? timeString.slice(0, 5) : null;
  }, []);

  const handleSettingsClick = () => {
    if (onTabChange) {
      onTabChange('primary');
    }
  };

  const getTime = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response: BksTimeResponse = await BksApi.getTime(deviceId);

      setDawnTime(formatTimeHHmm(response?.dawnTime));
      setDuskTime(formatTimeHHmm(response?.duskTime));

      if (response?.time) {
        const wallTime = response.time.split("+")[0];
        const timeMoment = moment(wallTime, "YYYY-MM-DDTHH:mm:ss");

        setDeviceTime(timeMoment);
        timeMomentRef.current = timeMoment.clone();
        setCurrentTime(timeMoment.format("DD.MM.YYYY HH:mm:ss"));

        const utcMatch = response.time.match(/([+-])(\d{2}):\d{2}$/);
        if (utcMatch) {
          const sign = utcMatch[1];
          const hours = utcMatch[2];
          setUserTimezone(`${sign}${hours}`);
        }
      } else {
        setDeviceTime(null);
        timeMomentRef.current = null;
        setCurrentTime("");
        setUserTimezone(null);
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      if (timeMomentRef.current) {
        intervalRef.current = setInterval(() => {
          const updatedMoment = timeMomentRef.current?.clone().add(1, "seconds");
          if (updatedMoment) {
            timeMomentRef.current = updatedMoment;
            setCurrentTime(updatedMoment.format("DD.MM.YYYY HH:mm:ss"));
          }
        }, 1000);
      }
    } catch (error: unknown) {
      console.error("Ошибка загрузки времени БКС", error);
      toaster.push(
        NotificationFactory.error("Ошибка получения времени БКС"),
        {placement: "topEnd"}
      );
      setDeviceTime(null);
      timeMomentRef.current = null;
      setCurrentTime("");
      setUserTimezone(null);
    } finally {
      setLoading(false);
    }
  }, [deviceId, formatTimeHHmm]);

  const readSyncStatus = useCallback(async (): Promise<void> => {
    setSyncLoading(true);
    try {
      const response: SyncParamsResponse = await PrimaryClockApi.getSyncParams(deviceId);
      setNtpSyncEnabled(response.ntpSyncEnabled);
    } catch (error: unknown) {
      console.error('Ошибка при получении статуса синхронизации:', error);
      toaster.push(
        NotificationFactory.error("Ошибка получения статуса NTP"),
        { placement: "topEnd" }
      );
    } finally {
      setSyncLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    getTime();
    readSyncStatus();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [getTime, readSyncStatus]);

  const handleRefresh = useCallback(() => {
    getTime();
    readSyncStatus();
  }, [getTime, readSyncStatus]);

  return (
    <>
      <div className="fieldset-wrapper bks-time-section mb-4">
        <div className="legend-title main-legend-title">Первичные часы</div>
        <section className="primary-clock p-1 pb-2">
          <div className="row mx-0">
            <div className="col-12 text-end">
              <UpdateButton loading={loading || syncLoading} onRefresh={handleRefresh}/>
            </div>
          </div>

          <div className="row align-items-center pt-1 mx-0">
            <div className="col-12 col-lg-4">
              <InputGroup style={{marginBottom: "5px"}}>
                <InputGroup.Addon style={{width: 130}}>
                  Время и дата
                </InputGroup.Addon>
                <Input
                  className="bg-green"
                  readOnly
                  value={currentTime}
                />
              </InputGroup>
            </div>

            <div className="col-12 col-sm-6 col-lg-3">
              <InputGroup style={{width: "100%", maxWidth: 220}} className="mb-1">
                <InputGroup.Addon>Час. пояс</InputGroup.Addon>
                <Input
                  value={userTimezone ?? ""}
                  style={{textAlign: "center"}}
                  readOnly
                />
              </InputGroup>
            </div>
            <div
              className="col-6 col-sm-3 col-lg mb-1"
            >
              <InputGroup>
                <InputGroup.Addon>Восход:</InputGroup.Addon>
                <Input
                  className="bg-violet"
                  style={{width: "100%"}}
                  readOnly
                  value={dawnTime ?? ""}
                />
              </InputGroup>
            </div>

            <div
              className="col-6 col-sm-3 col-lg mb-1"
            >
              <InputGroup>
                <InputGroup.Addon>Закат:</InputGroup.Addon>
                <Input
                  className="bg-violet"
                  style={{width: "100%"}}
                  readOnly
                  value={duskTime ?? ""}
                />
              </InputGroup>
            </div>
          </div>
          <div className="row mt-2 mx-0">
            <div className="col-xxl-4 col-xl-6 col-md-12">
              <div className="sync-toggle-item">
                <Toggle
                  color="blue"
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  loading={syncLoading}
                  checked={ntpSyncEnabled}
                  size="sm"
                >
                  <span className="toggle-text">
                    Синхронизация по NTP {ntpSyncEnabled ? "включена" : "отключена"}
                  </span>
                </Toggle>
              </div>
            </div>
          </div>
          <div className="settings mt-0">
            <a
              className="settings-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleSettingsClick();
              }}
            >
              <Whisper
                placement="topStart"
                controlId="control-id-hover"
                trigger="hover"
                speaker={getTooltip('Перейти в настройки')}>
                <GearSettingsIcon className="gear-icon"></GearSettingsIcon>
              </Whisper>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default BksTimeSection;
